import { useEffect, useCallback } from 'react';
import { getSocket, disconnectSocket } from '../services/socket';

interface AutoSubscriberAction {
  id: number;
  configId: number;
  targetUserId: string;
  status: string;
  error?: string;
  createdAt: string;
  vkData?: {
    userName: string;
    userUrl: string;
    userPhoto: string;
  } | null;
}

interface AutoSubscriberStatus {
  configId: number;
  todaySubscriptions: number;
  isActive: boolean;
}

interface AutoSubscriberError {
  configId: number;
  error: string;
}

export interface AutoSubscriberProgress {
  configId: number;
  phase: 'searching' | 'processing' | 'waiting' | 'completed' | 'error';
  message: string;
  details?: {
    usersFound?: number;
    usersProcessed?: number;
    usersSkipped?: number;
    skippedReasons?: { reason: string; count: number }[];
    nextRunIn?: number;
    // Absolute deadline + initial duration for the local 1Hz countdown during
    // the `waiting` phase. See useWaitingCountdown.
    waitUntilMs?: number;
    waitMs?: number;
  };
}

interface UseAutoSubscriberWebSocketProps {
  onAction?: (data: { configId: number; action: AutoSubscriberAction; vkData?: any }) => void;
  onStatus?: (data: AutoSubscriberStatus) => void;
  onError?: (data: AutoSubscriberError) => void;
  onProgress?: (data: AutoSubscriberProgress) => void;
}

export const useAutoSubscriberWebSocket = ({
  onAction,
  onStatus,
  onError,
  onProgress,
}: UseAutoSubscriberWebSocketProps) => {
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    if (onAction) socket.on('autoSubscriber:action', onAction);
    if (onStatus) socket.on('autoSubscriber:status', onStatus);
    if (onError) socket.on('autoSubscriber:error', onError);
    if (onProgress) socket.on('autoSubscriber:progress', onProgress);

    return () => {
      if (onAction) socket.off('autoSubscriber:action', onAction);
      if (onStatus) socket.off('autoSubscriber:status', onStatus);
      if (onError) socket.off('autoSubscriber:error', onError);
      if (onProgress) socket.off('autoSubscriber:progress', onProgress);
    };
  }, [onAction, onStatus, onError, onProgress]);

  const disconnect = useCallback(() => {
    disconnectSocket();
  }, []);

  return { disconnect };
};
